import { useCallback, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { BuildingLibraryIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import type { Conversation } from '@/hooks/useAllConversations';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import PausedChip from '@/components/lock/PausedChip';
import ConversationListItem from './ConversationListItem';

const COLLAPSE_STORAGE_KEY = 'flock_chat_sidebar_collapsed';

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

interface GroupNode {
  groupId: string;
  groupName: string;
  conversations: Conversation[];
  unread: number;
  latest: number;
}

interface OrgNode {
  orgId: string;
  orgName: string;
  groups: GroupNode[];
  unread: number;
  latest: number;
}

function latestMillis(c: Conversation): number {
  return c.lastMessageAt ? new Date(c.lastMessageAt).getTime() : 0;
}

interface Props {
  conversations: Conversation[];
  loading: boolean;
  error: string | null;
  selectedKey: string | null;
  onSelect: (conversation: Conversation) => void;
  headerAction?: React.ReactNode;
}

/**
 * Cross-org conversation tree: Organization (collapsible) → community group
 * (collapsible) → conversation threads. Selecting a thread in another org
 * rotates the org context automatically — no switcher needed.
 */
export default function ConversationSidebar({
  conversations,
  loading,
  error,
  selectedKey,
  onSelect,
  headerAction,
}: Props) {
  const { isOrgLocked } = useOrgLockState();
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);

  const toggleCollapsed = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(Array.from(next)));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.groupName.toLowerCase().includes(q) ||
        c.organizationName.toLowerCase().includes(q) ||
        (c.lastMessageText?.toLowerCase().includes(q) ?? false)
    );
  }, [conversations, search]);

  // Build Org → Group → conversations tree, ordered by recency at each level
  const tree = useMemo((): OrgNode[] => {
    const orgMap = new Map<string, OrgNode>();
    for (const conv of filtered) {
      let org = orgMap.get(conv.organizationId);
      if (!org) {
        org = { orgId: conv.organizationId, orgName: conv.organizationName, groups: [], unread: 0, latest: 0 };
        orgMap.set(conv.organizationId, org);
      }
      let group = org.groups.find((g) => g.groupId === conv.groupId);
      if (!group) {
        group = { groupId: conv.groupId, groupName: conv.groupName, conversations: [], unread: 0, latest: 0 };
        org.groups.push(group);
      }
      group.conversations.push(conv);
      group.unread += conv.unreadCount;
      group.latest = Math.max(group.latest, latestMillis(conv));
      org.unread += conv.unreadCount;
      org.latest = Math.max(org.latest, latestMillis(conv));
    }
    const orgs = Array.from(orgMap.values());
    for (const org of orgs) {
      org.groups.sort((a, b) => b.latest - a.latest);
      // conversations arrive recency-sorted already
    }
    orgs.sort((a, b) => b.latest - a.latest);
    return orgs;
  }, [filtered]);

  const searching = search.trim().length > 0;

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-r border-flock-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-flock-gray-200 p-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-flock-navy-300" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations"
            className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 py-2 pl-9 pr-3 text-sm text-flock-navy-900 outline-none placeholder:text-flock-navy-300 focus:border-flock-navy-400"
          />
        </div>
        {headerAction}
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {loading && (
          <div className="space-y-2 p-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex animate-pulse items-center gap-3 px-2 py-2">
                <div className="h-11 w-11 rounded-full bg-flock-navy-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-2/3 rounded bg-flock-navy-100" />
                  <div className="h-3 w-1/2 rounded bg-flock-navy-50" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="m-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="p-4 text-center text-sm text-flock-navy-400">
            {search ? 'No conversations match your search.' : 'No conversations yet.'}
          </div>
        )}

        {!loading &&
          tree.map((org) => {
            const orgKey = `org:${org.orgId}`;
            // While searching, force everything open so matches are visible
            const orgCollapsed = !searching && collapsed.has(orgKey);
            return (
              <div key={org.orgId} className="mb-1">
                <button
                    onClick={() => toggleCollapsed(orgKey)}
                    className="flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-left hover:bg-flock-navy-50"
                  >
                    {orgCollapsed ? (
                      <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" />
                    ) : (
                      <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" />
                    )}
                    <BuildingLibraryIcon className="h-4 w-4 shrink-0 text-flock-navy-500" />
                    <span className="min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wide text-flock-navy-700">
                      {org.orgName}
                    </span>
                    {isOrgLocked(org.orgId) && <PausedChip />}
                    {orgCollapsed && org.unread > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-flock-navy-600 px-1.5 text-[10px] font-semibold text-white">
                        {org.unread > 99 ? '99+' : org.unread}
                      </span>
                    )}
                </button>

                {!orgCollapsed &&
                  org.groups.map((group) => {
                    const groupKey = `group:${group.groupId}`;
                    const groupCollapsed = !searching && collapsed.has(groupKey);
                    return (
                      <div key={group.groupId}>
                        <button
                          onClick={() => toggleCollapsed(groupKey)}
                          className="flex w-full items-center gap-1.5 rounded-lg py-1.5 pl-5 pr-2 text-left hover:bg-flock-navy-50"
                        >
                          {groupCollapsed ? (
                            <ChevronRightIcon className="h-3 w-3 shrink-0 text-flock-navy-300" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3 shrink-0 text-flock-navy-300" />
                          )}
                          <span className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
                            {group.groupName}
                          </span>
                          {groupCollapsed && group.unread > 0 && (
                            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-flock-navy-600 px-1.5 text-[10px] font-semibold text-white">
                              {group.unread > 99 ? '99+' : group.unread}
                            </span>
                          )}
                        </button>
                        {!groupCollapsed && (
                          <div className="space-y-0.5">
                            {group.conversations.map((conv) => (
                              <ConversationListItem
                                key={conv.key}
                                conversation={conv}
                                selected={conv.key === selectedKey}
                                onSelect={onSelect}
                                indent
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}
      </div>
    </div>
  );
}
